var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import conf from "../config";
import { deepCamelCaseKeys } from "../utils";
import { LoginError, MembershipType, UserRole, } from "../types";
function isNvaValid(_a) {
    var nva = _a.notValidAfter;
    /*
    
      Note:   Do NOT use for security measures
  
      This method is dependent upon the client machine's internal time.
  
    */
    var now = Date.now();
    return !nva || nva.getTime() >= now;
}
function filterActiveMembershipPermissions(activeMembership) {
    /*
        Note: This method is to affect UX only, and is NOT a security measure;
              security cannot be enforced in client-side code.
        
        To prevent a poor UX, we simply remove the permissions to limit interaction.
        
        For example, when a disabled user DOES have permissions (i.e. if this step is removed), they
        can see views such as the Users list, and interact with the Invite Users modal. However, as
        soon as they make an API call to fetch/update data, they would be redirected to the Projects
        list. Removing permissions limits the users' access to such "buggy" UI components.
      
    */
    var isValid = isNvaValid(activeMembership);
    return isValid ? activeMembership : __assign(__assign({}, activeMembership), { permissions: [] });
}
export function getActiveMembership(userAppMetadata) {
    var _a;
    var activeMembership = (_a = userAppMetadata.memberships) === null || _a === void 0 ? void 0 : _a.find(
    // Below is so we can add future membership types without breaking
    // the active membership type.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    function (_a) {
        var type = _a.type;
        return type === MembershipType.ORGANIZATION;
    });
    if (!activeMembership) {
        throw new Error("User has no active membership");
    }
    return filterActiveMembershipPermissions(activeMembership);
}
export function checkIfEmployee(decodedIdTokenPayload) {
    var usedEmployeeLoginFlow = decodedIdTokenPayload.acr === conf.LIONBRIDGE_OIDC_ACR;
    var hasEmployeeRole = getAppMetadataFromPayload(decodedIdTokenPayload).memberships.some(function (membership) { return !conf.CONTRIBUTOR_ROLES.includes(membership.role); });
    return usedEmployeeLoginFlow || hasEmployeeRole;
}
export function parseIdToken(token) {
    var base64UrlPayload = token.split(".")[1];
    var base64Payload = base64UrlPayload.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(window
        .atob(base64Payload)
        .split("")
        .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(""));
    return deepCamelCaseKeys(JSON.parse(jsonPayload));
}
export function getLogoutRedirectUrl(decodedIdTokenPayload) {
    var userAppMetadata = getAppMetadataFromPayload(decodedIdTokenPayload);
    var activeMembership = getActiveMembership(userAppMetadata);
    var isEmployeeAccount = checkIfEmployee(decodedIdTokenPayload);
    var telusInternationalOrgId = conf.getAuthConfig().telusInternationalOrgId;
    var isTelusInternationalOrgMember = activeMembership.id === telusInternationalOrgId;
    var _a = conf.getAuthConfig(), employeeLogoutRedirectUrl = _a.employeeLogoutRedirectUrl, contributorLogoutRedirectUrl = _a.contributorLogoutRedirectUrl, defaultLogoutRedirectUrl = _a.defaultLogoutRedirectUrl;
    // Only users (not admins) belonging to the Lionbridge Organization who are NOT employees are
    // redirected back to CMP after logout
    if (!isEmployeeAccount &&
        isTelusInternationalOrgMember &&
        (activeMembership.role === UserRole.ORGANIZATION_USER || activeMembership.role === UserRole.CONTRIBUTOR)) {
        return contributorLogoutRedirectUrl;
    }
    if (isTelusInternationalOrgMember && (activeMembership.role === UserRole.ORGANIZATION_ADMIN || activeMembership.role === UserRole.EMPLOYEE)) {
        return employeeLogoutRedirectUrl;
    }
    return defaultLogoutRedirectUrl;
}
export function getAppMetadataFromPayload(payload) {
    // Reason: Identital will put the (e.g. ap) app_metadata in key `${payload.aud}/ap/app_metadata`
    // which is converted to camelCase before being sent to this function. `aud` is different for ever environment
    // something/ap/app_metadata -> somethingApAppMetadata, something/cmp/app_metadata -> somethingCmpAppMetadata
    var appMetadataKey = Object.keys(payload).find(function (key) { return key.endsWith("AppMetadata"); });
    if (!appMetadataKey) {
        throw new Error("Invalid JWT Payload");
    }
    var appMetadata = payload[appMetadataKey];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = deepCamelCaseKeys(appMetadata), memberships = _a.memberships, rest = __rest(_a, ["memberships"]);
    if (!memberships) {
        throw new Error(LoginError.NonAuthorized);
    }
    var formattedMemberships = memberships.map(function (_a) {
        var nva = _a.notValidAfter, restMembership = __rest(_a, ["notValidAfter"]);
        return (__assign(__assign({}, restMembership), { notValidAfter: nva ? new Date(nva) : undefined }));
    });
    return __assign(__assign({}, rest), { authalId: payload.sub, memberships: formattedMemberships });
}
